import { useEffect, useMemo, useState } from "react";
import { CalculationType } from "model/Tip";
import { useTranslation } from "react-i18next";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import type { SegmentOption } from "components/designV2/segmentcontroller/segmentcontroller";
import { formatTipDate } from "../../logic/tips.date.logic";
import { TipsDistributionFooter } from "./tips.distributionFooter.component";
import { TipsDistributionHourRows } from "./tips.distributionHourRows.component";
import {
  toEditableTips,
  toHourDistributionDrafts,
  toInitialTeamPercentages,
  toResetDistributionDrafts,
  toTeamRows,
  toWorkGroupPayload,
  updateEditableTipWorkerAmount,
} from "./tips.distributionModal.logic";
import {
  DistributionCard,
  DistributionDate,
  DistributionDescription,
  DistributionLabel,
  DistributionOverlay,
  DistributionScrollArea,
  DistributionSectionTitle,
  DistributionStatus,
  DistributionTitle,
  SalesPeriodPanel,
} from "./tips.distributionModal.styled";
import type {
  DistributionMode,
  EditableTipEntry,
  EditableTipsByDate,
  HandleTeamPercentageChangeParams,
  HandleWorkerAmountChangeParams,
  SalesPeriodMode,
  TeamPercentages,
  TeamRows,
  TipsDistributionModalProps,
} from "./tips.distributionModal.types";
import { TipsDistributionTeamRows } from "./tips.distributionTeamRows.component";

export const TipsDistributionModal = (
  props: TipsDistributionModalProps,
): JSX.Element | null => {
  const {
    calculationResult,
    isOpen,
    isSaving,
    onClose,
    onRecalculateTeamDistribution,
    onSaveHourDistribution,
    onSaveTeamDistribution,
    onUndoDistribution,
    tip,
  } = props;
  const { i18n, t } = useTranslation();
  const [salesPeriodMode, setSalesPeriodMode] =
    useState<SalesPeriodMode>("entireDay");
  const [distributionMode, setDistributionMode] =
    useState<DistributionMode>("hour");
  const [editableTips, setEditableTips] = useState<EditableTipsByDate>(() =>
    toEditableTips({ calculationResult }),
  );
  const [teamPercentages, setTeamPercentages] = useState<TeamPercentages>({});

  const teamRows = useMemo((): TeamRows => {
    if (!calculationResult) return [];
    return toTeamRows(calculationResult);
  }, [calculationResult]);
  const salesPeriodOptions = useMemo<SegmentOption<SalesPeriodMode>[]>(
    () => [
      {
        value: "entireDay",
        label: t("Tips.SalesPeriodEntireDay"),
      },
    ],
    [t],
  );
  const distributionOptions = useMemo<SegmentOption<DistributionMode>[]>(
    () => [
      {
        value: "hour",
        label: t("Tips.DistributionByHour"),
      },
      {
        value: "team",
        label: t("Tips.DistributionByTeam"),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (!isOpen) return;
    setEditableTips(toEditableTips({ calculationResult }));
  }, [calculationResult, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setTeamPercentages(toInitialTeamPercentages(teamRows));
  }, [isOpen, teamRows]);

  useEffect(() => {
    if (!isOpen || !tip) return;
    setDistributionMode(
      tip.calculationSettings?.type === CalculationType.Workgroup
        ? "team"
        : "hour",
    );
  }, [isOpen, tip]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !tip || !calculationResult) return null;

  const editableTipEntries: EditableTipEntry[] = Object.entries(editableTips);
  const totalTipsAmount = calculationResult.companyTips.reduce(
    (sum, tip) => sum + tip.amount,
    0,
  );
  const remainingAmount = editableTipEntries.reduce(
    (sum, [, tip]) => sum + tip.remainingAmount,
    0,
  );
  const canUndoDistribution = editableTipEntries.some(
    ([, editableTip]) =>
      editableTip.remainingAmount < editableTip.amount ||
      editableTip.workers.some((worker) => worker.tipAmount > 0),
  );
  const totalPercentage = Object.values(teamPercentages).reduce(
    (sum, percentage) => sum + (Number(percentage) || 0),
    0,
  );
  const percentageLeftToSplit = Math.max(0, 100 - totalPercentage);
  const isLockDistributionDisabled =
    distributionMode === "team" && totalPercentage !== 100;

  const getWorkGroupPayload = (): ReturnType<typeof toWorkGroupPayload> =>
    toWorkGroupPayload({ teamPercentages, teamRows });

  const handleWorkerAmountChange = ({
    date,
    value,
    workerIndex,
  }: HandleWorkerAmountChangeParams): void => {
    if (!/^\d*$/.test(value)) return;
    setEditableTips((currentTips) =>
      updateEditableTipWorkerAmount({
        currentTips,
        date,
        value,
        workerIndex,
      }),
    );
  };

  const handleTeamPercentageChange = ({
    value,
    workGroupId,
  }: HandleTeamPercentageChangeParams): void => {
    if (!/^\d*$/.test(value)) return;
    setTeamPercentages((currentValues) => ({
      ...currentValues,
      [workGroupId]: value,
    }));
  };

  const handleTeamPercentageBlur = (): void => {
    if (totalPercentage !== 100) return;
    void onRecalculateTeamDistribution(getWorkGroupPayload());
  };

  const handleLockDistribution = (): void => {
    if (distributionMode === "team") {
      void onSaveTeamDistribution(getWorkGroupPayload());
      return;
    }

    void onSaveHourDistribution(toHourDistributionDrafts(editableTipEntries));
  };

  const handleUndoDistribution = (): void => {
    void onUndoDistribution(toResetDistributionDrafts(editableTipEntries));
  };

  return (
    <DistributionOverlay onClick={onClose}>
      <DistributionCard
        role="dialog"
        aria-modal="true"
        aria-label={t("Tips.DistributionTitle")}
        onClick={(event) => event.stopPropagation()}
      >
        <DistributionScrollArea>
          <DistributionTitle>{t("Tips.DistributionTitle")}</DistributionTitle>
          <DistributionDate>
            {formatTipDate({
              dateValue: tip.date,
              locale: i18n.language,
            })}
          </DistributionDate>

          <SalesPeriodPanel>
            <DistributionLabel>{t("Tips.SalesPeriod")}</DistributionLabel>
            <DistributionDescription>
              {t("Tips.SalesPeriodDescription")}
            </DistributionDescription>
            <SegmentController
              options={salesPeriodOptions}
              value={salesPeriodMode}
              onChange={setSalesPeriodMode}
            />
          </SalesPeriodPanel>

          <DistributionSectionTitle>
            {t("Tips.TipDistribution")}
          </DistributionSectionTitle>
          <SegmentController
            options={distributionOptions}
            value={distributionMode}
            onChange={setDistributionMode}
            tone="dark"
          />

          {distributionMode === "hour" ? (
            <TipsDistributionHourRows
              editableTipEntries={editableTipEntries}
              onWorkerAmountChange={handleWorkerAmountChange}
            />
          ) : (
            <TipsDistributionTeamRows
              teamPercentages={teamPercentages}
              teamRows={teamRows}
              onTeamPercentageBlur={handleTeamPercentageBlur}
              onTeamPercentageChange={handleTeamPercentageChange}
            />
          )}

          {distributionMode === "team" && totalPercentage !== 100 && (
            <DistributionStatus>
              {t("Tips.TeamPercentageHint", {
                percentage: percentageLeftToSplit,
              })}
            </DistributionStatus>
          )}
        </DistributionScrollArea>

        <TipsDistributionFooter
          canUndoDistribution={canUndoDistribution}
          distributionMode={distributionMode}
          isLockDistributionDisabled={isLockDistributionDisabled}
          isSaving={isSaving}
          percentageLeftToSplit={percentageLeftToSplit}
          remainingAmount={remainingAmount}
          totalTipsAmount={totalTipsAmount}
          onClose={onClose}
          onLockDistribution={handleLockDistribution}
          onUndoDistribution={handleUndoDistribution}
        />
      </DistributionCard>
    </DistributionOverlay>
  );
};
